import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  ConnectionHandler,
} from 'react-relay'
import * as Yup from 'yup'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { addDays, addHours } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { FormikConfig, useFormikContext } from 'formik'

import { EventFormDatePicker } from '@newComponents/Event/EventFormDatePicker'
import {
  EventOwnerPicker,
  EventOwnerChange,
} from '@newComponents/Event/EventOwnerPicker'
import { pickInitialEventOwnerId } from '@newComponents/Event/eventOwnerOptions'

import { XStack, YStack } from 'tamagui'
import { H2, Paragraph, Text } from '@newComponents/Text'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import { FormTimezonePicker } from '@newComponents/Form/FormTimezonePicker'
import { Link } from '@newComponents/Link'

import { Form } from '@newComponents/Form/Form'
import { FormField } from '@newComponents/Form/FormField'
import { FormTextInput } from '@newComponents/Form/FormTextInput'
import { FormSubmitButton } from '@newComponents/Form/FormSubmitButton'

import { DEFAULT_TIMEZONE } from '@data/timezones'
import { INCLUDE_IN_EVENTS_FEED } from '@data/config/meetup'
import { useMutation } from '@data/useMutation'
import { useScreenMessages } from '@data/i18n'
import {
  getRecentEventOwnerIds,
  rememberEventOwner,
} from '@data/recentEventOwners'
import { useFlashMessage } from '@newComponents/DynamicIsland/useFlashMessage'

import { EventNewContentQuery } from './__generated__/EventNewContentQuery.graphql'
import { EventNewContentCreateEventMutation } from './__generated__/EventNewContentCreateEventMutation.graphql'
import { EventNewContent_user$key } from './__generated__/EventNewContent_user.graphql'

type FormValues = {
  endAt: Date
  name: string
  ownerId: string
  startAt: Date
  timeZone: string
}

const loadCatalog = (locale: string) =>
  import(
    /* webpackChunkName: "locale-event-new-screen-[request]" */ `./locales/${locale}.po`
  )

const useValidationSchema = () => {
  const { t } = useLingui()
  return useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(t`Required`),
        timeZone: Yup.string().required(t`Required`),
        startAt: Yup.date()
          .typeError(t`Invalid Date`)
          .required(t`Required`),
        endAt: Yup.date()
          .typeError(t`Invalid Date`)
          .required(t`Required`)
          .when('startAt', (startAt: string, schema: Yup.DateSchema) => {
            if (schema.isValidSync(startAt)) {
              return schema.min(startAt, t`is before Start`)
            } else {
              return schema
            }
          }),
        ownerId: Yup.string()
          .uuid()
          .min(1)
          .required(t`Required`),
      }),
    [t]
  )
}

/**
 * Keeps the time zone in step with the chosen Guild's default time zone.
 */
const EventOwnerField = ({
  user,
}: {
  user: React.ComponentProps<typeof EventOwnerPicker>['user']
}) => {
  const formikContext = useFormikContext<FormValues>()

  const handleOwnerChange = useCallback(
    (owner: EventOwnerChange) => {
      const timeZone = owner.defaultTimeZone || DEFAULT_TIMEZONE

      if (formikContext.values.timeZone !== timeZone) {
        void formikContext.setFieldValue('timeZone', timeZone, false)
      }
    },
    [formikContext]
  )

  return <EventOwnerPicker user={user} onOwnerChange={handleOwnerChange} />
}

/**
 * Applies a Guild requested in the URL once it shows up in the (possibly
 * refetched) list of Guilds the person can create Events for, unless they
 * already picked an owner themselves.
 */
const RequestedOwnerSync = ({
  ownerRowId,
  timeZone,
}: {
  ownerRowId: string | undefined
  timeZone: string | null | undefined
}) => {
  const { values, touched, setFieldValue } = useFormikContext<FormValues>()

  useEffect(() => {
    if (!ownerRowId || touched.ownerId || values.ownerId === ownerRowId) {
      return
    }

    void setFieldValue('ownerId', ownerRowId, false)
    void setFieldValue('timeZone', timeZone || DEFAULT_TIMEZONE, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerRowId])

  return null
}

export const EventNewContent = () => {
  useScreenMessages(loadCatalog)
  const { t } = useLingui()
  const validationSchema = useValidationSchema()
  const [searchParams] = useSearchParams()
  const requestedGuild = searchParams.get('guild')

  const query = useLazyLoadQuery<EventNewContentQuery>(
    graphql`
      query EventNewContentQuery {
        viewer {
          ...EventNewContent_user
        }
      }
    `,
    {},
    { fetchPolicy: 'store-and-network' }
  )

  const fragment = useFragment<EventNewContent_user$key>(
    graphql`
      fragment EventNewContent_user on User {
        id
        rowId

        ...EventOwnerPicker_user

        guildsForNewEvents(first: 500) {
          nodes {
            rowId
            slugId
            defaultTimeZone
          }
        }
      }
    `,
    query.viewer
  )

  const createEventMutation = useMutation<EventNewContentCreateEventMutation>(
    graphql`
      mutation EventNewContentCreateEventMutation(
        $input: CreateEventWithSlugMutationInput!
      ) {
        createEventWithSlug(input: $input) {
          event {
            prettyUrl

            owner {
              ... on Guild {
                ...GuildEventsPastCard_guild
                ...GuildEventsUpcomingCard_guild
              }
            }
          }

          query {
            ...EventsScreenList_query
          }
        }
      }
    `
  )

  const navigate = useNavigate()
  const { showFlash } = useFlashMessage()

  const guildNodes = useMemo(
    () =>
      (fragment?.guildsForNewEvents.nodes ?? []).filter(
        (guild): guild is NonNullable<typeof guild> => !!guild
      ),
    [fragment]
  )

  // A Guild passed in the URL (from a Guild page) that the person can't
  // create Events for gets an explanation under the picker.
  const requestedGuildNode = useMemo(
    () =>
      requestedGuild
        ? guildNodes.find(
            (guild) =>
              guild.slugId === requestedGuild || guild.rowId === requestedGuild
          )
        : undefined,
    [guildNodes, requestedGuild]
  )

  const buildInitialValues = (): FormValues | null => {
    if (!fragment?.rowId) {
      return null
    }

    const ownerId = pickInitialEventOwnerId({
      requestedGuild,
      recentOwnerIds: getRecentEventOwnerIds(),
      guilds: guildNodes,
      userRowId: fragment.rowId,
    })

    const timeZone =
      guildNodes.find((guild) => guild.rowId === ownerId)?.defaultTimeZone ||
      DEFAULT_TIMEZONE

    const nowBareHour =
      timeZone === DEFAULT_TIMEZONE
        ? new Date().setMinutes(0, 0, 0)
        : utcToZonedTime(
            zonedTimeToUtc(new Date().setMinutes(0, 0, 0), DEFAULT_TIMEZONE),
            timeZone
          )

    const startAt = addHours(addDays(nowBareHour, 1), 1)
    const endAt = addHours(addDays(nowBareHour, 1), 3)

    return {
      name: '',
      timeZone,
      startAt,
      endAt,
      ownerId,
    }
  }

  // Built once so refetches don't wipe what the person typed; created late
  // if the viewer only arrives with the network response.
  const [initialValues, setInitialValues] = useState<FormValues | null>(
    buildInitialValues
  )

  useEffect(() => {
    if (!initialValues && fragment?.rowId) {
      setInitialValues(buildInitialValues())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, fragment?.rowId])

  const handleSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      const ownerType = values.ownerId === fragment?.rowId ? 'users' : 'guilds'
      const response = await createEventMutation({
        variables: {
          input: {
            ownerType,
            ...values,
            startAt: zonedTimeToUtc(
              values.startAt,
              values.timeZone
            ).toISOString(),
            endAt: zonedTimeToUtc(values.endAt, values.timeZone).toISOString(),
          },
        },
        updater: (store) => {
          const root = store.getRoot()

          const connections = [
            ConnectionHandler.getConnection(
              root,
              'EventsScreenList_query_upcomingEvents',
              {
                timeSelection: 'UPCOMING',
                ...(INCLUDE_IN_EVENTS_FEED
                  ? {
                      filter: { allowGlobalListing: { equalTo: true } },
                    }
                  : {
                      filter: {
                        and: [
                          {
                            not: {
                              metadata: {
                                containsKey: 'meetupEventID',
                              },
                            },
                          },
                          { allowGlobalListing: { equalTo: true } },
                        ],
                      },
                    }),
              }
            ),
          ]

          const viewerRecord = fragment?.id ? store.get(fragment.id) : null

          if (viewerRecord) {
            connections.push(
              ConnectionHandler.getConnection(
                viewerRecord,
                'EventsHostingList_user_managedEvents',
                { timeSelection: 'UPCOMING' }
              ),
              ConnectionHandler.getConnection(
                viewerRecord,
                'EventsHostingList_user_managedEvents',
                { timeSelection: 'PAST' }
              )
            )

            // Plain (non-connection) lists of the viewer's Events, such as
            // the home screen's "You're hosting", refetch on next view.
            viewerRecord.invalidateRecord()
          }

          if (ownerType === 'users') {
            const userRecord = store.get(
              btoa(JSON.stringify(['users', fragment?.rowId]))
            )

            if (userRecord) {
              connections.push(
                ConnectionHandler.getConnection(
                  userRecord,
                  'UserEventsUpcomingSection_user_upcomingEvents',
                  { timeSelection: 'UPCOMING' }
                ),
                ConnectionHandler.getConnection(
                  userRecord,
                  'UserEventsPastSection_user_pastEvents',
                  { timeSelection: 'PAST' }
                )
              )
            }
          }

          if (ownerType === 'guilds') {
            const guildRecord = store.get(
              btoa(JSON.stringify(['guilds', values.ownerId]))
            )

            if (guildRecord) {
              connections.push(
                ConnectionHandler.getConnection(
                  guildRecord,
                  'GuildEventsPastCard_guild_pastEvents',
                  {
                    timeSelection: 'PAST',
                    orderBy: ['START_AT_DESC', 'ID_ASC'],
                  }
                ),

                ConnectionHandler.getConnection(
                  guildRecord,
                  'GuildEventsUpcomingCard_guild_upcomingEvents',
                  {
                    timeSelection: 'UPCOMING',
                    orderBy: ['START_AT_ASC', 'ID_ASC'],
                  }
                )
              )

              // The Guild page's own Event lists refetch on next view.
              guildRecord.invalidateRecord()
            }
          }

          connections.map((connection) => connection?.invalidateRecord())
        },
      })

      const event = response.createEventWithSlug.event

      if (event) {
        rememberEventOwner(values.ownerId)
        showFlash(
          t`Event created! Find it anytime under Events you host.`,
          'success'
        )
        navigate(`/events/${event.prettyUrl}/settings`)
      } else {
        showFlash(t`Failed to create Event`, 'error')
        console.error('SERVER SIDE ERROR FROM EVENT CREATION', response)
      }
    },
    [createEventMutation, fragment?.id, fragment?.rowId, navigate, showFlash, t]
  )

  if (!query?.viewer || !fragment || !initialValues) {
    // Server rendering is always signed out; only the client knows.
    if (typeof __SERVER__ !== 'undefined' && __SERVER__) {
      return null
    }

    return (
      <YStack gap='$4' alignItems='center' padding='$4'>
        <Paragraph textAlign='center'>
          <Trans>Sign in to create an Event.</Trans>
        </Paragraph>

        <Link
          href={`/sign-in?authRedirectPath=${encodeURIComponent(
            requestedGuild
              ? `/events/new?guild=${encodeURIComponent(requestedGuild)}`
              : '/events/new'
          )}`}
          color='$primaryColor'
          fontWeight='$6'
        >
          <Trans>Sign in</Trans>
        </Link>
      </YStack>
    )
  }

  const hasGuilds = guildNodes.length > 0
  const requestedGuildUnavailable = !!requestedGuild && !requestedGuildNode

  return (
    <YStack gap='$6'>
      <XStack gap='$4' alignItems='center'>
        <EmojiIcon name='spiral-calendar' size='$9' />
        <YStack gap='$1' flex={1}>
          <Text size='$7' fontWeight='$7'>
            <Trans>Create an Event</Trans>
          </Text>
          <Paragraph size='$4' color='$colorMuted'>
            <Trans>Set up a new Event for your community</Trans>
          </Paragraph>
        </YStack>
      </XStack>

      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <RequestedOwnerSync
          ownerRowId={requestedGuildNode?.rowId}
          timeZone={requestedGuildNode?.defaultTimeZone}
        />

        <YStack gap='$5'>
          <FormField name='name' label={t`Title`}>
            <FormTextInput placeholder={t`Your event title`} />
          </FormField>

          <YStack gap='$2'>
            <FormField name='ownerId' label={t`Who's hosting?`}>
              <EventOwnerField user={fragment} />
            </FormField>

            {requestedGuildUnavailable ? (
              <Paragraph size='$4' color='$colorMuted'>
                <Trans>
                  You can&apos;t create Events for that Guild. Ask one of its
                  Organizers to make you an Organizer or Editor.
                </Trans>
              </Paragraph>
            ) : null}

            {/* The notice above already explains a refused ?guild= link. */}
            {!hasGuilds && !requestedGuildUnavailable ? (
              <Paragraph size='$4' color='$colorMuted'>
                <Trans>
                  To host for a Guild, you need to be one of its Organizers or
                  Editors. Ask an Organizer to add you, or{' '}
                  <Link
                    href='/guilds/new'
                    color='$primaryColor'
                    textDecorationLine='underline'
                    size='$4'
                    fontWeight='$6'
                  >
                    create a Guild
                  </Link>
                  .
                </Trans>
              </Paragraph>
            ) : null}
          </YStack>

          <YStack gap='$2'>
            <H2 fontWeight='$5'>
              <Trans>When and how long?</Trans>
            </H2>

            <YStack gap='$4'>
              <EventFormDatePicker />
            </YStack>
          </YStack>

          <FormField name='timeZone' label={t`Time Zone`}>
            <FormTimezonePicker />
          </FormField>

          <XStack gap='$4' alignItems='center'>
            <XStack flex={1}>
              <Paragraph color='$color' fontWeight='$3' size='$4'>
                <Trans>
                  Your Event stays unlisted until you list it, so you can add
                  more details first.
                </Trans>
              </Paragraph>
            </XStack>

            <FormSubmitButton>
              <FormSubmitButton.Text>
                <Trans>Continue</Trans>
              </FormSubmitButton.Text>
            </FormSubmitButton>
          </XStack>
        </YStack>
      </Form>
    </YStack>
  )
}

export default EventNewContent
